'use client';

import { usePathname } from '@/i18n/navigation';
import { ReactNode, useMemo, Suspense, createContext, useContext } from 'react';
import dynamic from 'next/dynamic';
import { useAtomValue } from 'jotai';
import { isRummyLoadedOnceAtom } from '@/app/[locale]/(main)/(non-activity)/rummy/store';
import { menuTabUrl } from '@/const/page-url';

// 需要 keep-alive 的路由列表
const KEEP_ALIVE_ROUTES = [menuTabUrl.home, menuTabUrl.casino, menuTabUrl.fantasy, menuTabUrl.rummy, menuTabUrl.sports];

// Route Active Context
interface RouteActiveContextValue {
  isActive: boolean;
  route: string;
}

const RouteActiveContext = createContext<RouteActiveContextValue | undefined>(undefined);

/**
 * Hook to access route active state
 * Use this hook inside keep-alive route components to react to route activation
 * 
 * @example
 * ```tsx
 * function MyPage() {
 *   const { isActive } = useRouteActive();
 *   const { refetch } = useQuery(...);
 *   
 *   useEffect(() => {
 *     if (isActive) {
 *       // Refresh data when route becomes active
 *       refetch();
 *     }
 *   }, [isActive, refetch]);
 *   
 *   return <div>Content</div>;
 * }
 * ```
 */
export function useRouteActive() {
  const context = useContext(RouteActiveContext);

  // If not in RouteKeepAlive context, return safe defaults
  if (!context) {
    return {
      isActive: true, // Assume active if not in RouteKeepAlive
      route: '',
    };
  }

  return context;
}

// 动态导入所有需要 keep-alive 的页面组件（客户端组件）
const HomePageClient = dynamic(() => import('@/app/[locale]/(main)/(non-activity)/components/home-page-client'), {
  ssr: false,
});

const CasinoPageClient = dynamic(() => import('@/app/[locale]/(main)/(non-activity)/casino/components/casino-page-client'), {
  ssr: false,
});

const FantasyPageClient = dynamic(() => import('@/app/[locale]/(main)/(non-activity)/fantasy/components/fantasy-page-client'), {
  ssr: false,
});

const SportsPageClient = dynamic(() => import('@/app/[locale]/(main)/(non-activity)/sports/components/sports-page-client'), {
  ssr: false,
});

const RummyPageClient = dynamic(() => import('@/app/[locale]/(main)/(non-activity)/rummy/components/rummy-page-client'), {
  ssr: false,
});

// 包装 HomePage，添加滚动位置管理
function HomePageWrapper() {
  return (
    <Suspense fallback={null}>
      <HomePageClient />
    </Suspense>
  );
}

// 路由到组件的映射
const routeComponentMap: Record<string, () => ReactNode> = {
  [menuTabUrl.home]: () => <HomePageWrapper />,
  [menuTabUrl.casino]: () => (
    <Suspense fallback={null}>
      <CasinoPageClient />
    </Suspense>
  ),
  [menuTabUrl.fantasy]: () => (
    <Suspense fallback={null}>
      <FantasyPageClient />
    </Suspense>
  ),
  [menuTabUrl.rummy]: () => (
    <Suspense fallback={null}>
      <RummyPageClient />
    </Suspense>
  ),
  [menuTabUrl.sports]: () => (
    <Suspense fallback={null}>
      <SportsPageClient />
    </Suspense>
  ),
};

interface RouteKeepAliveProps {
  children: ReactNode;
}

/**
 * 路由 Keep-Alive 组件
 * 实现类似 Vue keep-alive 的功能：
 * - Layout 常驻
 * - 预先渲染所有路由组件（客户端组件）
 * - display 切换显示/隐藏
 * 注意：所有需要 keep-alive 的路由组件都必须是客户端组件（'use client'）
 */
export function RouteKeepAlive({ children }: RouteKeepAliveProps) {
  const pathname = usePathname();
  const currentPath = pathname || '/';
  const isRummyLoadedOnce = useAtomValue(isRummyLoadedOnceAtom);
  // 判断当前路由是否需要 keep-alive
  // 对于 /casino/* 的子路由，也使用 keep-alive（除了 /casino/games 和 /casino/groups）
  const shouldKeepAlive = useMemo(() => {
    // 如果是 /casino 的子路由（除了 games 和 groups），也使用 keep-alive
    if (currentPath.startsWith('/casino')) {
      const skipRoutes = ['/casino/games', '/casino/groups'];
      if (skipRoutes.some(route => currentPath.startsWith(route))) {
        return false;
      }
      return true;
    }
    return KEEP_ALIVE_ROUTES.includes(currentPath);
  }, [currentPath]);

  // const currentRouteKeyPath = useMemo(() => getKeepAliveRouteKey(currentPath), [currentPath]);

  if (!shouldKeepAlive) {
    // 不需要 keep-alive 的路由（如 /casino/games），直接渲染 Next.js 的 children
    return <>{children}</>;
  }

  // 获取当前路由对应的 keep-alive 路由键（用于匹配组件）
  // 对于 /casino/* 的子路由，都映射到 '/casino'
  const getKeepAliveRouteKey = (path: string): string => {
    if (path.startsWith('/casino')) {
      const skipRoutes = ['/casino/games', '/casino/groups'];
      if (skipRoutes.some(route => path.startsWith(route))) {
        return path;
      }
      return '/casino';
    }
    return path;
  };

  const currentRouteKey = getKeepAliveRouteKey(currentPath);

  // 对于需要 keep-alive 的路由，忽略 Next.js 传入的 children（Server Component）
  // 直接渲染我们预先定义的客户端组件，避免 Server Component 重新执行
  // 使用 opacity + position 而不是 display: none，确保 Safari 中 iframe 能正常加载
  return (
    <>
      {KEEP_ALIVE_ROUTES.map((route) => {
        const isActive = route === currentRouteKey;
        const Component = routeComponentMap[route];

        if (!Component) return null;

        // 对于包含 iframe 的路由（rummy），使用 keepLayoutWhenHidden 方式
        // 这样可以确保 iframe 在隐藏时也能继续加载和渲染
        let keepLayoutWhenHidden = route === '/rummy';
        keepLayoutWhenHidden = !isRummyLoadedOnce && keepLayoutWhenHidden ? true : false;
        // keepLayoutWhenHidden 为 true 时用 visibility + zIndex 隐藏（保留布局，iframe 可继续加载）；为 false 时用 display: none
        const hiddenStyle: React.CSSProperties = keepLayoutWhenHidden
          ? {
            // iframe hidden style
            opacity: 0,
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            visibility: 'visible',
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: -1,
          }
          : {
            display: 'none',
            visibility: 'hidden',
            zIndex: -1,
          };

        const visibleStyle: React.CSSProperties = {
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          visibility: 'visible',
          zIndex: 0,
        };

        return (
          <RouteActiveContext.Provider
            key={route}
            value={{ isActive, route }}
          >
            <div
              className="flex-1"
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                ...(isActive ? visibleStyle : hiddenStyle),
              }}
            >
              <Component />
            </div>
          </RouteActiveContext.Provider>
        );
      })}
    </>
  );
}
